import {
  Box,
  constants,
  Divider,
  Field,
  FieldRow,
  FieldSet,
  Form,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import type { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useState } from 'react';
import { messages } from '../messages';
import { Gender, MeasurementUnit, SizeGuideData } from '../types';
import { APPAREL_PRODUCT_TYPE_IDS } from './constants';
import { FitIndicator } from './FitIndicator';
import { Header } from './Header';
import { HowToChoose } from './HowToChoose';
import { Model } from './Model';
import styles from './SizeGuideModalContent.module.scss';
import { SizingInformation } from './SizingInformation';

export interface SizeGuideModalContentProps {
  sizeGuideData: SizeGuideData;
  initialGender?: Gender;
  initialMeasurementUnit?: MeasurementUnit;
  productTypeId: string;
}

export const SizeGuideModalContent: React.FC<SizeGuideModalContentProps> = ({
  sizeGuideData,
  initialGender,
  initialMeasurementUnit = MeasurementUnit.In,
  productTypeId,
}) => {
  const intl = useIntl();

  const [{ locale }] = useUserInfo<UserInfo>();

  const {
    internationalConversions,
    fitIndicator,
    productMeasurements,
    howToChooseSize,
    model,
  } = sizeGuideData;

  // Some products do not have a male or a female fit effectively providing just one
  // gender's size guide. Male is the default set in the size guide so the following
  // sets the default gender to male if the model details for male exists otherwise
  // we use female.
  const defaultGender =
    initialGender ?? (model?.male ? Gender.Male : Gender.Female);

  const initialFormValues = {
    gender: defaultGender,
    measurementUnit: initialMeasurementUnit,
  };

  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(
    initialMeasurementUnit
  );

  const [gender, setGender] = useState<Gender>(defaultGender);

  const genderOptions = [
    { value: Gender.Male, label: intl.formatMessage(messages.men) },
    { value: Gender.Female, label: intl.formatMessage(messages.women) },
  ];

  const modelValue = model?.[gender];

  const measurementUnitOptions = [
    { value: MeasurementUnit.In, label: intl.formatMessage(messages.inches) },
    {
      value: MeasurementUnit.Cm,
      label: intl.formatMessage(messages.centimeters),
    },
  ];

  const getLocalisedHelpCenterLink = `https://help.redbubble.com/hc/${
    locale === 'en' ? 'en-us' : locale
  }/articles/115000118466`;

  const productMeasurementsTableDataWithoutCallback =
    productMeasurements[measurementUnit];

  const firstColumnIsSize =
    productMeasurementsTableDataWithoutCallback.columns[0].dataIndex === 'size';

  const productMeasurementsTableData = firstColumnIsSize
    ? productMeasurementsTableDataWithoutCallback
    : {
        ...productMeasurementsTableDataWithoutCallback,
        columns: productMeasurementsTableDataWithoutCallback.columns.map(
          (col, i) => ({
            ...col,
            onHeaderCell: (record) => ({ ...record, isBold: i === 0 }),
          })
        ),
      };

  const handleChange = (e) => {
    setGender(e.values.gender);
    setMeasurementUnit(e.values.measurementUnit);
  };

  /**
   * Currently, only internationalConversions are indicative of non-unisex products.
   * If/when other conditions become available, this logic should be expanded to include new conditions.
   */
  const isUnisex = !internationalConversions;

  const isApparel = APPAREL_PRODUCT_TYPE_IDS.includes(productTypeId);

  return (
    <Box>
      <Header />
      <Divider color={constants.COLOR_NEUTRAL_100} marginY="xl" />

      <Box className={styles.measurementsSection}>
        <Form
          profile={constants.MOBILE}
          initialValues={initialFormValues}
          onChange={handleChange}
        >
          {() => (
            <FieldSet style={{ paddingLeft: 'revert', paddingRight: 'revert' }}>
              <Box className={styles.formFieldRow}>
                {!isUnisex && (
                  <FieldRow>
                    <Field
                      type="button-toggle"
                      name="gender"
                      options={genderOptions}
                      multiple={false}
                      fluid
                      label={intl.formatMessage(messages.gender)}
                      hideLabel
                    />
                  </FieldRow>
                )}

                <FieldRow>
                  <Field
                    type="button-toggle"
                    name="measurementUnit"
                    options={measurementUnitOptions}
                    multiple={false}
                    fluid
                    label={intl.formatMessage(messages.measurementUnit)}
                    hideLabel
                  />
                </FieldRow>
              </Box>
              {/* find your fit */}
              {fitIndicator && (
                <Box paddingBottom="xl" paddingTop="l">
                  <FitIndicator
                    indicatorValue={fitIndicator.value}
                    premium={fitIndicator.premium}
                  />
                </Box>
              )}
            </FieldSet>
          )}
        </Form>

        {/* Product measurements table */}
        <Box paddingBottom="xxl">
          <Text type="display5" element="h3">
            {intl.formatMessage(messages.productMeasurements)}
          </Text>
          <Table
            fixedFirstColumn
            headingFirstColumn
            rowKey="size"
            columns={productMeasurementsTableData.columns}
            rows={productMeasurementsTableData.rows}
          />
        </Box>

        {/* how to choose size */}
        {howToChooseSize && (
          <Box paddingBottom="xl">
            <HowToChoose
              imageUrl={howToChooseSize.imageUrl}
              instructions={howToChooseSize.instructions}
            />
          </Box>
        )}

        {/* model measurements */}
        {modelValue && (
          <Box paddingBottom="xl">
            <Model
              imageUrl={modelValue.imageUrl}
              modelMeasurements={modelValue.modelMeasurements}
              modelSize={modelValue.modelSize}
            />
          </Box>
        )}

        {/* measurements footnote */}
        {isApparel && (
          <Text type="body2">
            *{intl.formatMessage(messages.measurementsFootnote)}
          </Text>
        )}
      </Box>

      <Divider color={constants.COLOR_NEUTRAL_100} marginY="xl" />

      <Box className={styles.measurementsFooter}>
        {/* sizing FAQ */}
        <SizingInformation localisedLink={getLocalisedHelpCenterLink} />

        {/* returns */}
        <Stack direction={constants.VERTICAL} paddingY="xxl">
          <Text type="display5" element="h3">
            {intl.formatMessage(messages.returnsTitle)}
          </Text>
          <Text type="body"> {intl.formatMessage(messages.returnsBody)} </Text>
        </Stack>
      </Box>
    </Box>
  );
};

export const SizeGuideModalContentLoadingState: React.FC<{}> = () => (
  <Box data-testid="sizeGuidePlaceholder">
    <Stack
      direction={constants.VERTICAL}
      alignItems="center"
      spacing="xs"
      paddingTop="xxl"
    >
      <Skeleton height={83} width={140} animated roundedCorners />
      <Box mt={8}>
        <Skeleton height={29} width={115} animated roundedCorners />
      </Box>
    </Stack>

    <Divider color={constants.COLOR_NEUTRAL_100} marginY="xl" />
    <Box className={styles.measurementsSection}>
      <Box className={styles.formFieldRow}>
        <Box flex="1" height={40} marginX="s" paddingBottom="l">
          <Skeleton height={40} animated roundedCorners />
        </Box>
        <Box flex="1" height={40} marginX="s" paddingBottom="l">
          <Skeleton height={40} animated roundedCorners />
        </Box>
      </Box>
    </Box>

    <Box paddingBottom="xxl">
      <Box marginBottom="s">
        <Skeleton height={19} width={176} animated roundedCorners />
      </Box>
      <Skeleton height={250} animated roundedCorners />
    </Box>

    <Divider color={constants.COLOR_NEUTRAL_100} marginY="xl" />

    <Box className={styles.measurementsFooter}>
      <Stack direction={constants.HORIZONTAL} spacing="s" alignItems="center">
        <Skeleton height={26} width={196} animated roundedCorners />
      </Stack>
      <Stack direction={constants.VERTICAL} paddingY="xxl">
        <Box marginBottom="s">
          <Skeleton height={19} width={172} animated roundedCorners />
        </Box>
        <Skeleton height={19} width={300} animated roundedCorners />
      </Stack>
    </Box>
  </Box>
);
